/**
 * KraftStories — HEADER ANALYTICS
 * ============================================================================
 * Named GA4 events for every interactive element in the header, on all nine
 * header variants (global + eight city headers).
 *
 * WHY THIS IS A SEPARATE FILE, AND WHY IT USES NAMED EVENTS
 * --------------------------------------------------------
 * analytics-events.js already tracks `nav_menu_hover`, `location_change`,
 * `modal_open`, `phone_click`, `email_click` and `cta_click`, and it already
 * attaches `page_category` + `page_location_city` to every one of them. That
 * data is being collected and then thrown away: the GA4 property has exactly
 * four registered event-scoped custom dimensions (lead_type, session_campaign,
 * session_medium, session_source), so every other parameter is invisible in
 * reports and unreadable through Windsor. `modal_open` showing 3 users while
 * ab_*_modal_* shows hundreds is that same wall.
 *
 * So the primary signal here is carried by the EVENT NAME, which is always
 * readable with no GA4 admin work at all. Parameters carry the detail and
 * become sliceable once the dimensions are registered — but nothing here
 * returns zero while that is pending. This is the mistake that made
 * blog_cta_* report nothing for two days; it is not repeated.
 *
 * Everything is read out of the DOM. There is no Liquid change anywhere in the
 * header, which means this cannot break a header that 64% of pageviews depend
 * on. The one hook it relies on already exists for the location selector's own
 * behaviour: [data-location-toggle][data-current-location].
 *
 * CAPTURE PHASE, ON PURPOSE
 * -------------------------
 * Every listener is registered with capture: true on `document`. The header is
 * full of `href="javascript:void(0)"` links and handlers that call
 * preventDefault/stopPropagation (mega-menu.js, location-selector.js, the
 * modal openers). A bubble-phase listener silently loses those clicks — that is
 * exactly how the blogcta experiment lost every arm-A click and made the modal
 * arm look like the worst performer. Capture phase sees the click first, always.
 *
 * THE DENOMINATOR
 * ---------------
 * `header_view` fires once per pageview carrying header_variant. Without it
 * there is no click-through rate: GA4's own `page_view` is sent by Shopify's
 * Google tag and cannot carry a custom parameter, so nothing else in the
 * property can tell you how many people SAW the Hawaii header.
 *
 * WHEN THE HEADER CHANGES, RE-CHECK THIS FILE
 * -------------------------------------------
 * Every selector here is a contract with markup that other people edit. Two
 * failure modes have already happened once each and are silent in both cases:
 * a selector that matches nothing (the click is simply never recorded), and two
 * selectors reading the same concept out of different attributes (menu_id came
 * from data-mega-trigger on opens and from the panel's id on clicks, so the two
 * events could not be joined). Neither shows up as an error - only as a number
 * that is quietly lower than it should be. Re-run the coverage check against
 * header-original.liquid after any nav rework.
 *
 * A NOTE ON header_variant vs page_location_city
 * ----------------------------------------------
 * These come from two independent implementations — detect-page-location.liquid
 * (which decides which header renders) and gtm-datalayer.liquid (which sets
 * window.__kraft_page_location_city). They can disagree. Both are recorded on
 * every event so that disagreement is visible in the data instead of silently
 * mis-attributing a header. header_variant is the authoritative one for
 * "which header did this person see", because it is read off the header itself.
 */

(function () {
  'use strict';

  /* WHY THIS IS A QUEUE AND NOT A FUNCTION CALL
     -------------------------------------------
     __kraftGa4Mirror pushes to dataLayer and then mirrors via gtag. For these
     events the gtag leg is the ONLY path to GA4: GTM has no tags for them, and
     __kraft_mirror_gtag_to_ga4 is false because a container IS configured. And
     mirrorGtagFlat begins `if (typeof window.gtag !== 'function') return;`.

     `gtag` is not defined by any inline script on this site — measured on
     /pages/hawaii/custom-rug-tufting, the only definition arrives with an
     external googletagmanager.com/gtag/js that does not START loading until
     718ms, while domContentLoadedEventEnd is 713ms and this deferred script
     executes at domInteractive, 473ms. So at the moment header_view fires,
     window.gtag does not exist yet and the event is dropped on the floor.

     Which it was: over 7 Aug, header_view reached 35 users against 97 page_view
     users — 36%. Worse than a flat undercount, that 36% is BIASED, because the
     only loads that got through were the slow ones where parsing dragged the
     deferred script past the tag's arrival. The denominator was quietly
     over-weighted toward slow devices, which are the least likely to convert.

     So: buffer until gtag exists, then flush in order. This is the same
     60-try/500ms pattern ksBlogEmit already uses in article-cta-bar.liquid —
     that file solved this exact problem for the blog CTA, and the fix belongs
     here for the same reason. The delay is ~250ms and GA4 timestamps on
     receipt, so nothing about the data changes except that it arrives. */
  var pending = [];
  var polling = false;
  var PENDING_MAX = 60;
  var FLUSH_TRIES = 60;
  var FLUSH_MS = 500;

  function ready() {
    return typeof window.__kraftGa4Mirror === 'function' && typeof window.gtag === 'function';
  }

  function fire(eventName, params) {
    try {
      window.__kraftGa4Mirror(eventName, params || {});
    } catch (e) {
      /* Analytics must never throw into a click handler. */
    }
  }

  function startPolling() {
    if (polling) return;
    polling = true;
    var tries = 0;
    (function poll() {
      if (ready()) {
        var q = pending;
        pending = [];
        polling = false;
        for (var i = 0; i < q.length; i++) fire(q[i][0], q[i][1]);
        return;
      }
      /* 30 seconds, then give up: a visitor with the tag blocked is never
         going to receive these, and an unbounded timer is a leak. */
      if (++tries > FLUSH_TRIES) { polling = false; pending = []; return; }
      setTimeout(poll, FLUSH_MS);
    })();
  }

  function emit(eventName, params) {
    if (ready() && !pending.length) { fire(eventName, params); return; }
    /* Cap the buffer so a blocked tag cannot grow it without limit. Order is
       preserved, which matters for header_view -> open -> click sequences. */
    if (pending.length < PENDING_MAX) pending.push([eventName, params || {}]);
    startPolling();
  }

  var DESKTOP_MIN_PX = 992; /* the theme's own lg breakpoint, same as ab-head */
  var HEADER_SEL = 'nav.navbar, .navbar, #header_section, .header';

  var headerEl = null;
  var variant = 'global';
  var scope = 'global';

  function text(el) {
    if (!el) return '';
    /* Prefer an inner <span>: nav links and buttons wrap their label in one,
       and taking textContent off the parent drags in the SVG arrow's
       whitespace and the mobile-only duplicate labels. */
    var span = el.querySelector('span:not([aria-hidden="true"])');
    var t = (span ? span.textContent : el.textContent) || '';
    return t.replace(/\s+/g, ' ').trim().substring(0, 100);
  }

  function href(el) {
    var h = el && el.getAttribute ? el.getAttribute('href') || '' : '';
    if (h.indexOf('javascript:') === 0) return '';
    return h.substring(0, 100);
  }

  function surface() {
    var w = window.innerWidth || document.documentElement.clientWidth || 0;
    return w >= DESKTOP_MIN_PX ? 'desktop' : 'mobile';
  }

  /** Params present on every event, so any of them can be a report dimension. */
  function base() {
    return {
      header_variant: variant,
      header_scope: scope,
      header_language: (document.documentElement.getAttribute('lang') || '').toLowerCase(),
      header_surface: surface(),
      page_path: window.location.pathname,
      page_category: window.__kraft_page_category || '',
      page_location_city: window.__kraft_page_location_city || ''
    };
  }

  function send(eventName, extra) {
    var p = base();
    if (extra) {
      Object.keys(extra).forEach(function (k) {
        if (extra[k] !== null && extra[k] !== undefined && extra[k] !== '') p[k] = extra[k];
      });
    }
    emit(eventName, p);
  }

  /* WHY SOME EVENTS GO OUT TWICE, ONCE WITH THE MENU IN THE NAME
     ------------------------------------------------------------
     The property has 4 registered custom dimensions and Windsor reads only
     event_name, so menu_id — collected on every open and click since day
     one — has never been visible in a report. Same wall the A/B framework
     hit; same fix: put the one dimension that matters in the name
     (header_menu_open_for_teams), keep the full detail in params for the
     day the dimensions get registered. The unsuffixed event still fires
     first so existing report queries keep working; per-menu names are
     additive. GA4 caps names at 40 chars — the longest base here is
     header_menu_link_click_ (23) + keys like for_teams; sanitize() also
     hard-caps the result. */
  function sanitizeKey(key) {
    return String(key || '').toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '').substring(0, 16);
  }

  function sendWithMenuSuffix(eventName, menuKey, extra) {
    send(eventName, extra);
    var k = sanitizeKey(menuKey);
    if (k) send((eventName + '_' + k).substring(0, 40), extra);
  }

  /* ==========================================================================
     WHICH HEADER IS THIS
     ========================================================================== */

  function resolveVariant() {
    var toggle = document.querySelector('[data-location-toggle]');
    var v = toggle ? (toggle.getAttribute('data-current-location') || '').trim().toLowerCase() : '';
    variant = v || 'global';
    scope = v ? 'localized' : 'global';
  }

  /* ==========================================================================
     header_view — the denominator, once per pageview
     ========================================================================== */

  function sendHeaderView() {
    var navItems = headerEl ? headerEl.querySelectorAll('.navbar-nav > .nav-item') : [];
    var menus = headerEl ? headerEl.querySelectorAll('[data-mega-trigger]') : [];

    send('header_view', {
      /* Whether the "Find a workshop" CTA rendered (shipped from the hdrcta
         test). It is scoped to non-localised pages and skipped on the home
         page, where the hero carries the same CTA — 'no' there is expected. */
      has_find_cta: headerEl && headerEl.querySelector('.ks-hdr-find-item') ? 'yes' : 'no',
      nav_item_count: navItems.length,
      menu_count: menus.length
    });
  }

  /* ==========================================================================
     TOP-LEVEL NAV
     ========================================================================== */

  function navPosition(el) {
    var li = el.closest('.nav-item');
    if (!li || !li.parentNode) return 0;
    var sibs = li.parentNode.children;
    for (var i = 0; i < sibs.length; i++) if (sibs[i] === li) return i + 1;
    return 0;
  }

  function onNavClick(el) {
    var isMega = el.hasAttribute && el.hasAttribute('data-mega-trigger');
    send('header_nav_click', {
      nav_label: text(el),
      nav_type: isMega ? 'mega' : 'link',
      nav_href: isMega ? '' : href(el),
      nav_menu_id: isMega ? el.getAttribute('data-mega-trigger') : '',
      nav_position: navPosition(el)
    });
  }

  /* ==========================================================================
     MEGA MENU OPENS

     Detected from aria-expanded on the trigger rather than from a click,
     because mega-menu.js opens on desktop HOVER and on mobile CLICK, and only
     the attribute is true for both. Watching the attribute also means the
     event fires when the menu genuinely appears, not when someone brushes past
     the trigger — the existing nav_menu_hover event cannot distinguish those.

     open_method is inferred from whether a click landed on the trigger in the
     last 400ms.
     ========================================================================== */

  var lastTriggerClickAt = 0;
  var lastTriggerClicked = null;

  /* ONE KEY PER MENU, USED BY BOTH EVENTS.

     The trigger carries data-mega-trigger="activities" while the panel it
     controls carries id="megaMenuActivities". Reading one on the open event and
     the other on the click event makes menu_id two different vocabularies and
     header_menu_link_click / header_menu_open — the follow-through rate, which
     is the whole point of tracking opens — cannot be joined at all.

     The trigger's aria-controls is the panel id, so one pass at init gives an
     authoritative id -> key map. The trigger key wins because it is the shorter,
     stabler string and it is what the theme's own code is written against. */
  var menuKeyById = {};

  function buildMenuKeyMap() {
    var triggers = headerEl.querySelectorAll('[data-mega-trigger]');
    for (var i = 0; i < triggers.length; i++) {
      var panelId = triggers[i].getAttribute('aria-controls');
      var key = triggers[i].getAttribute('data-mega-trigger');
      if (panelId && key) menuKeyById[panelId] = key;
    }
  }

  /* ABANDONMENT — the missing half of the funnel.
     open -> link click is follow-through; open -> close with no click is a
     visitor who read the menu and found nothing worth clicking. Until now
     that outcome was only recorded on mobile (the Back button); on desktop
     the menu closes on mouseleave and the retreat vanished. State below is
     per menu key: when it opened, and when a link inside it was last
     clicked. The observer already sees both open and close as aria-expanded
     flips; the close branch decides which outcome it was. */
  var menuOpenedAt = {};   /* key -> ms timestamp of the open */
  var menuOpenMethod = {}; /* key -> 'click' | 'hover' */
  var menuLinkClickAt = {}; /* key -> ms timestamp of last link click inside */

  var ABANDON_MIN_DWELL_MS = 1000;

  function watchMenuOpens() {
    var triggers = headerEl.querySelectorAll('[data-mega-trigger]');
    if (!triggers.length || typeof MutationObserver !== 'function') return;

    var obs = new MutationObserver(function (records) {
      for (var i = 0; i < records.length; i++) {
        var t = records[i].target;
        var key = t.getAttribute('data-mega-trigger') || '';

        if (t.getAttribute('aria-expanded') === 'true') {
          var wasClick =
            lastTriggerClicked === t && Date.now() - lastTriggerClickAt < 400;

          menuOpenedAt[key] = Date.now();
          menuOpenMethod[key] = wasClick ? 'click' : 'hover';

          sendWithMenuSuffix('header_menu_open', key, {
            menu_id: key,
            nav_label: text(t),
            open_method: wasClick ? 'click' : 'hover'
          });
          continue;
        }

        /* CLOSE. Decide whether it converted or was abandoned. */
        if (!menuOpenedAt[key]) continue;
        var dwell = Date.now() - menuOpenedAt[key];
        var clicked = menuLinkClickAt[key] && menuLinkClickAt[key] >= menuOpenedAt[key];
        var method = menuOpenMethod[key] || 'hover';
        menuOpenedAt[key] = 0;

        if (clicked) continue; /* follow-through — already recorded as a click */

        /* A hover flyby (brushed the trigger, menu closed in under a second)
           is not a considered rejection; counting it as abandonment would
           bury the real signal. Click-opens count at any dwell — a click is
           always intent. */
        if (method === 'hover' && dwell < ABANDON_MIN_DWELL_MS) continue;

        sendWithMenuSuffix('header_menu_abandon', key, {
          menu_id: key,
          open_method: method,
          dwell_ms: Math.min(dwell, 120000)
        });
      }
    });

    for (var i = 0; i < triggers.length; i++) {
      obs.observe(triggers[i], { attributes: true, attributeFilter: ['aria-expanded'] });
    }
  }

  /* ==========================================================================
     LINKS INSIDE THE MEGA MENUS  (~60 of them)

     link_group is the .mega-menu__title above the link — "Signature Crafts",
     "More Workshops", "Formats", "Enterprise", "Corporate Gifting" and so on.
     Without it, "Terrarium" appears in two different columns and the two are
     indistinguishable.

     Labels come out of translation files, so link_href is recorded alongside
     link_label: on a Japanese Hawaii page the label changes and the href does
     not. Group by href when comparing across languages.
     ========================================================================== */

  /* View All is a <button> on the global header (it opens the workshop finder
     modal instead of navigating), so an 'a.' prefixed selector misses it.
     Matched on class alone so both the button and the anchor version on
     location pages are captured.

     The Originals card grid IS present — three image links and three CTAs at
     lines ~1100-1188 of header-original.liquid. It was briefly dropped from
     this list on the belief it had been deleted; verified against the current
     markup and restored. Six clickables in the Originals menu depend on it.

     mega-menu__location-card is the studio photo grid added to the localised
     Workshops menu — a real navigation target with no list wrapper. */
  var MENU_LINK_SEL = [
    'a.mega-menu__link',
    'a.mega-menu__title-link',
    'a.mega-menu__location-card',
    'a.originals-card__cta',
    'a.originals-card__image-link',
    '.originals-view-all__link',
    '.localized-mobile-flat-menu a'
  ].join(',');

  function menuIdFor(link) {
    var menu = link.closest('.mega-menu');
    if (menu) {
      var id = menu.getAttribute('id') || '';
      /* Normalise to the trigger key so this joins header_menu_open. */
      return menuKeyById[id] || id || menu.getAttribute('data-mega-name') || 'mega';
    }
    if (link.closest('.localized-mobile-flat-menu')) return 'localized_flat_menu';
    return '';
  }

  function groupFor(link) {
    var section = link.closest('.mega-menu__section') || link.closest('.localized-mobile-flat-menu__section');
    var title = section ? section.querySelector('.mega-menu__title') : null;
    if (!title) {
      var col = link.closest('.mega-menu__column');
      title = col ? col.querySelector('.mega-menu__title') : null;
    }
    /* The title itself may be a link; its own text is the group name. */
    return title ? text(title) : '';
  }

  function linkPosition(link) {
    /* Location cards and Originals cards are not wrapped in <li> — they are
       direct children of a grid container — so fall back to the link itself.
       Without this they all reported position 0 and the grid order was lost. */
    var node = link.closest('.mega-menu__item') || link.closest('li') || link;
    if (!node || !node.parentNode) return 0;
    var sibs = node.parentNode.children;
    for (var i = 0; i < sibs.length; i++) if (sibs[i] === node) return i + 1;
    return 0;
  }

  function kindOf(link) {
    var c = link.classList;
    if (c.contains('originals-view-all__link')) return 'view_all';
    if (c.contains('mega-menu__title-link')) return 'group_title';
    if (c.contains('mega-menu__location-card')) return 'location_card';
    if (c.contains('originals-card__image-link')) return 'originals_card_image';
    if (c.contains('originals-card__cta')) return 'originals_card_cta';
    return 'list_item';
  }

  function onMenuLinkClick(link) {
    var kind = kindOf(link);
    var menuKey = menuIdFor(link);
    menuLinkClickAt[menuKey] = Date.now(); /* the close branch reads this */
    sendWithMenuSuffix('header_menu_link_click', menuKey, {
      menu_id: menuKey,
      /* View All sits outside any titled section, so it has no column heading
         to inherit; naming its own bucket keeps link_group non-empty. */
      link_group: kind === 'view_all' ? 'view_all' : groupFor(link),
      link_label: text(link),
      link_href: href(link),
      link_position: linkPosition(link),
      link_kind: kind
    });
  }

  /* ==========================================================================
     THE REST OF THE HEADER CHROME
     ========================================================================== */

  function onLogoClick(el) {
    send('header_logo_click', {
      link_href: href(el),
      link_kind: el.classList.contains('navbar-home-icon-link') ? 'home_icon' : 'wordmark'
    });
  }

  function onSearchOpen() {
    send('header_search_open', {});
  }

  function onAccountClick(el) {
    send('header_account_click', {
      link_href: href(el),
      logged_in: el.querySelector('.ks-account-dot') ? 'yes' : 'no'
    });
  }

  function onCartClick(el) {
    var countEl = document.querySelector('[data-cart-count], [data-mobile-cart-count]');
    var n = countEl
      ? parseInt(countEl.getAttribute('data-cart-count') || countEl.getAttribute('data-mobile-cart-count') || '0', 10)
      : 0;
    send('header_cart_click', {
      cart_count: isNaN(n) ? 0 : n,
      link_kind: el.classList.contains('mobile-cart-trigger') ? 'mobile' : 'desktop'
    });
  }

  function onDrawerToggle(el) {
    /* Read BEFORE the theme's own handler flips it: capture phase runs first,
       so aria-expanded still holds the pre-click state. */
    var wasOpen = el.getAttribute('aria-expanded') === 'true';
    send('header_drawer_toggle', { drawer_action: wasOpen ? 'close' : 'open' });
  }

  function onLocationOpen(el) {
    var wasOpen = el.getAttribute('aria-expanded') === 'true';
    if (wasOpen) return; /* only count opening; closing is not intent */
    send('header_location_open', { from_location: variant });
  }

  function onLocationSelect(el) {
    var to = (el.getAttribute('data-location') || '').trim().toLowerCase();
    send('header_location_select', {
      from_location: variant,
      to_location: to || 'global',
      link_href: href(el),
      /* Someone picking the city they are already in is a navigation failure,
         not a location change — worth being able to count separately. */
      is_same_location: to === variant ? 'yes' : 'no'
    });
  }

  function onLanguageSwitch(el) {
    send('header_language_switch', {
      from_language: (document.documentElement.getAttribute('lang') || '').toLowerCase(),
      to_language: (el.getAttribute('value') || '').toLowerCase()
    });
  }

  function onFindCtaClick(el) {
    send('header_cta_click', {
      cta_label: text(el),
      cta_kind: 'find_workshop',
      modal_id: el.getAttribute('data-modal-trigger') || ''
    });
  }

  function onContactClick(el) {
    send('header_contact_click', {
      cta_label: text(el),
      modal_id: el.getAttribute('data-modal-trigger') || ''
    });
  }

  /* The mobile mega menus open full-screen with a "Back to main menu" button.
     Pressing it is a distinct outcome from picking a link: the visitor opened
     the menu, read it, and chose nothing. Without this, that retreat is
     indistinguishable from a menu that was never opened. */
  function onMenuBack(el) {
    var menu = el.closest('.mega-menu');
    var id = menu ? (menu.getAttribute('id') || '') : '';
    send('header_menu_close', {
      menu_id: menuKeyById[id] || id,
      close_method: 'back_button'
    });
  }

  /* The story strip is its own <nav class="ks-story-strip"> BEFORE the navbar,
     so headerEl.contains() is false for it — matched without the containment
     check, like the ticker. Suffixed per link (not per menu: the strip IS one
     menu, its links are the dimension) so Windsor can rank Why KraftStories vs
     Blog vs Reviews clicks by event name alone.

     Shipped permanently 10 Aug 2026 (was the 'hdrstory' A/B arm B). This
     tracking never depended on the A/B framework and did not change on ship,
     so header_strip_click_* is a continuous series across the cutover — but
     expect roughly a 2x step up in volume, since the strip went from 50% of
     desktop traffic to 100%. Compare rates, not counts, across 10 Aug. */
  function stripLinkSlug(el) {
    var h = href(el) || '';
    var seg = h.split('?')[0].replace(/\/+$/, '').split('/').pop() || '';
    return sanitizeKey(seg || text(el));
  }

  function onStoryStripClick(el) {
    var slug = stripLinkSlug(el);
    send('header_strip_click', {
      link_label: text(el),
      link_href: href(el),
      link_slug: slug
    });
    if (slug) send(('header_strip_click_' + slug).substring(0, 40), { link_href: href(el) });
  }

  function onTickerClick(el) {
    send('header_ticker_click', {
      link_label: text(el),
      link_href: href(el),
      link_kind: el.classList.contains('ks-ticker__slide') ? 'mobile_slide' : 'desktop_item'
    });
  }

  function onDirectContact(el, method) {
    var raw = el.getAttribute('href') || '';
    send('header_contact_direct', {
      contact_method: method,
      /* The value itself, not the person's — these are the studio's published
         numbers, one of eight fixed strings. */
      contact_value: raw.replace(/^(tel:|mailto:)/i, '').split('?')[0].substring(0, 100),
      link_kind: el.closest('.mega-menu') ? 'mega_menu' : 'nav_bar'
    });
  }

  /* ==========================================================================
     ONE CAPTURE-PHASE LISTENER
     ========================================================================== */

  function onDocumentClick(e) {
    if (!headerEl) return;
    var t = e.target;
    if (!t || !t.closest) return;

    /* Scope to the header. The mega menus and the mobile drawer are inside the
       <nav>, so a single containment check covers everything. */
    var el;

    el = t.closest('[data-mega-trigger]');
    if (el && headerEl.contains(el)) {
      lastTriggerClicked = el;
      lastTriggerClickAt = Date.now();
      onNavClick(el);
      return;
    }

    el = t.closest('.location-dropdown__link');
    if (el && headerEl.contains(el)) { onLocationSelect(el); return; }

    el = t.closest('[data-location-toggle]');
    if (el && headerEl.contains(el)) { onLocationOpen(el); return; }

    el = t.closest('.language-switch__btn');
    if (el && headerEl.contains(el)) { onLanguageSwitch(el); return; }

    el = t.closest('.ks-ticker__item, .ks-ticker__slide');
    if (el) { onTickerClick(el); return; }

    /* Search lives in the strip since 10 Aug 2026. Two reasons this branch
       sits ABOVE the .ks-story-strip__link one and not with the other
       .nav-search-btn handling further down:

       1. The strip's search button also carries .ks-story-strip__link (for
          typography), so the generic strip branch would swallow it and log a
          header_strip_click_* instead of a search open.
       2. The .nav-search-btn branch below is guarded by headerEl.contains(),
          and the strip sits OUTSIDE headerEl — so that branch can never match
          an element in the strip.

       Firing onSearchOpen keeps header_search_open continuous across the
       move, which is the point: the navbar baseline was 1.4% of desktop
       header viewers, so the strip version is directly comparable.

       LOAD-ORDER DEPENDENCY (pre-existing, documented here because it is not
       obvious and a reordering of scripts.liquid would silently kill this
       event). kraft-script.js owns the delegated [data-modal-trigger] handler
       and calls e.stopImmediatePropagation() on every match — which would
       starve this listener, since both are capture-phase on `document` and
       the first one registered wins. It does not, because of WHEN each one
       registers: kraft-script.js registers inside its DOMContentLoaded
       callback, whereas this file is a deferred script, so at execution time
       document.readyState is already 'interactive' and init() runs
       immediately (see the bottom of this file). This listener is therefore
       in place first and runs first. If header-analytics.js ever stops being
       deferred, or starts waiting on DOMContentLoaded unconditionally, every
       modal-trigger event in this file goes silent. */
    el = t.closest('.ks-story-strip__search');
    if (el) { onSearchOpen(); return; }

    el = t.closest('.ks-story-strip__link');
    if (el) { onStoryStripClick(el); return; }

    el = t.closest('.ks-hdr-find-item a');
    if (el && headerEl.contains(el)) { onFindCtaClick(el); return; }

    el = t.closest('a[href^="tel:"], a[href^="mailto:"]');
    if (el && headerEl.contains(el)) {
      onDirectContact(el, (el.getAttribute('href') || '').toLowerCase().indexOf('mailto:') === 0 ? 'email' : 'phone');
      return;
    }

    el = t.closest('.mega-menu__back-btn, [data-mega-close]');
    if (el && headerEl.contains(el)) { onMenuBack(el); return; }

    el = t.closest(MENU_LINK_SEL);
    if (el && headerEl.contains(el)) { onMenuLinkClick(el); return; }

    el = t.closest('.navbar-brand, .navbar-home-icon-link');
    if (el && headerEl.contains(el)) { onLogoClick(el); return; }

    el = t.closest('.nav-search-btn');
    if (el && headerEl.contains(el)) { onSearchOpen(); return; }

    el = t.closest('.ks-account-btn, .mobile-account-trigger');
    if (el && headerEl.contains(el)) { onAccountClick(el); return; }

    el = t.closest('[data-cart-drawer-trigger]');
    if (el && headerEl.contains(el)) { onCartClick(el); return; }

    el = t.closest('.navbar-toggler');
    if (el && headerEl.contains(el)) { onDrawerToggle(el); return; }

    el = t.closest('[data-modal-trigger]');
    if (el && headerEl.contains(el) && /contact-modal$/.test(el.getAttribute('data-modal-trigger') || '')) {
      onContactClick(el);
      return;
    }

    /* Any remaining top-level nav link: Classes, Gifts, Team Building, About
       on the city headers, which are plain <a class="nav-link"> with no mega
       menu behind them. Checked last so it cannot swallow a more specific
       match above. */
    el = t.closest('.navbar-nav > .nav-item > a.nav-link');
    if (el && headerEl.contains(el)) { onNavClick(el); return; }
  }

  /* ==========================================================================
     INIT
     ========================================================================== */

  function init() {
    headerEl = document.querySelector(HEADER_SEL);
    if (!headerEl) return;

    resolveVariant();
    buildMenuKeyMap();   /* must run before any menu_id is read */
    sendHeaderView();
    watchMenuOpens();

    document.addEventListener('click', onDocumentClick, true);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();
